'use client';
import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useStoredSignupAccess } from '@/hooks/useStoredSignupAccess';
import { FilmGallery, CTACard } from '@/components/marketing';
import { SignUpModal } from '@/components/auth';
import { useTranslations as useTranslations$1 } from 'next-intl';
export function ShowcaseSection() {
    const t = useTranslations$1();
    const sectionRef = useScrollAnimation<HTMLElement>({
        animateChildren: true
    });
    const hasSignupAccess = useStoredSignupAccess();
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    return (<section ref={sectionRef} id="showcase" className="animate-on-scroll relative overflow-hidden bg-zinc-950 px-6 py-20 text-white md:px-8 md:py-32 lg:px-12">
      { /* Subtle background gradient */ }
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-zinc-950/50 to-transparent"/>

      { /* Subtle grid pattern overlay */ }
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]"/>

      <div className="relative mx-auto max-w-7xl">
        { /* Header - enhanced */ }
        <div className="mb-16 text-start md:mb-24 md:text-center">
          <h2 className="mb-6 text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl sm:leading-tight md:text-6xl lg:text-7xl">
            { /* Desktop: "See what filmmakers are" / "creating with Martini" */ }
            <span className="hidden bg-gradient-to-r from-white/95 to-white/80 bg-clip-text text-transparent md:block">
              {t("TDzZPq")}
            </span>
            <span className="hidden bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent md:mt-3 md:block">
              {t("hpd0wW")}
            </span>
            { /* Mobile: "See what filmmakers" / "are creating with Martini" */ }
            <span className="block bg-gradient-to-r from-white/95 to-white/80 bg-clip-text text-transparent md:hidden">
              {t("xewMpL")}
            </span>
            <span className="-mt-0.5 block bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent md:hidden">
              {t("utg3Gj")}
            </span>
          </h2>
          <p className="mx-auto mt-6 max-w-3xl text-start text-lg text-zinc-400 md:text-center md:text-xl">
            {t("TVLTJA")}
          </p>
        </div>

        { /* Film Gallery */ }
        <div className="mb-12 md:mb-16">
          <FilmGallery/>
        </div>

        { /* CTA Card */ }
        <CTACard className="mx-auto max-w-2xl text-start md:text-center">
          <h3 className="mb-3 text-xl font-semibold text-zinc-200 md:mb-4 md:text-xl lg:text-2xl">
            {t("EFBaRC")}
          </h3>
          <p className="mb-6 text-lg font-normal leading-relaxed text-zinc-200 md:text-lg lg:text-xl">
            {t("LO8UDT")}
          </p>
          {hasSignupAccess ? (<button type="button" onClick={()=>setShowSignUpModal(true)} className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-base font-semibold text-black transition-all duration-300 hover:scale-[1.02] hover:bg-white/95 active:scale-[0.98]">
              {t("PEa8RR")}
            </button>) : (<a href="https://form.typeform.com/to/VVwyeUrj" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-base font-semibold text-black transition-all duration-300 hover:scale-[1.02] hover:bg-white/95 active:scale-[0.98]">
              {t("UnR8YN")}
            </a>)}
        </CTACard>
      </div>

      <SignUpModal isOpen={showSignUpModal} onClose={()=>setShowSignUpModal(false)} returnPath="/projects" defaultMode="signup" variant="marketing"/>
    </section>);
}
